import { PublicPageShell, Section } from "../components/PublicPageShell";

export default function Methodology() {
  return (
    <PublicPageShell overline="How the analysis works" title="Methodology" updated="February 2026" testId="methodology-page">
      <Section heading="What SphereInvest AI does">
        <p>
          SphereInvest AI produces research insights, not personalised financial advice. It gathers public market data
          for a fixed universe of roughly 73 US and UK large-cap companies, computes standard indicators, and uses a
          large language model to explain — in plain English — why a company matches the criteria you selected and
          where the risks may sit. It is built to support, not replace, your own decisions.
        </p>
      </Section>

      <Section heading="Data inputs">
        <p>
          Market data comes from Yahoo Finance (retrieved via the open-source yfinance library): quotes, key
          fundamentals (PE, PEG, margins, ROE, debt/equity, revenue growth, dividend yield), one year of daily price
          history, and recent news headlines. This data is typically delayed or end-of-day, not real-time, and we
          cache it for up to 12 hours to respect source limits. Index and macro snapshots (S&amp;P 500, NASDAQ,
          FTSE 100, VIX, rates, FX) come from the same source.
        </p>
      </Section>

      <Section heading="Computed indicators">
        <p>
          From the cached price history we compute: simple moving averages (SMA50/SMA200), RSI(14), MACD, ATR,
          distance from the 52-week high/low, a simple trend label (uptrend/downtrend/sideways), and — for portfolios —
          volatility, beta, maximum drawdown, concentration and a diversification score. These are standard textbook
          formulas; no proprietary signals and no predictions.
        </p>
      </Section>

      <Section heading="How the AI is used">
        <p>
          AI research summaries, the research assistant, briefings and theses are generated by Anthropic&apos;s Claude
          model. Every AI output is grounded: the model is given only our cached fundamentals, computed technicals,
          peer comparisons and news headlines for the relevant tickers, and is instructed to analyse that data — never
          to give buy/sell instructions or price targets. Structured outputs (attractive factors, risk factors,
          valuation view, technical view) are cached, typically for 24 hours, so repeated views don&apos;t re-generate.
        </p>
      </Section>

      <Section heading="Limitations — read this">
        <ul className="list-disc pl-5 space-y-2">
          <li>No intraday or real-time data: quotes are delayed or end-of-day and cached up to 12 hours.</li>
          <li>Fixed universe of ~73 large caps — the scanner cannot see companies outside it.</li>
          <li>AI models can be wrong, out of date, or overconfident. Outputs are probabilistic text, not verified facts. Always check the underlying numbers.</li>
          <li>No forecasts: nothing here predicts prices. Simulator outputs are mathematical illustrations, not expectations.</li>
          <li>Fundamentals depend on the data source and may contain gaps or errors we cannot control.</li>
        </ul>
      </Section>
      <Section heading="Contact">
        <p>
          SphereInvest AI is operated by Three Spheres Healthcare Limited, a company registered in England and Wales
          (Company No. 14029879). Questions about this methodology or the data behind it:{" "}
          <a href="mailto:info@threesphereshealthcare.org" className="text-primary hover:underline">info@threesphereshealthcare.org</a>.
        </p>
      </Section>
    </PublicPageShell>
  );
}
